const CARD_COLLECTIONS = [
    '#shelf-list-product',
    '.swiper-wrapper',
    '.productList__products',
    '.products',
    '.tab__panel',
];

const CARD_STATES = {
    customPrice: 'product-item--custom-price',
    featuredDiscount: 'product-item--featured-discount',
    grid: 'product-item--grid',
    layout1: 'product-item--layout-1',
    carousel: 'product-item--carousel',
    tabs: 'product-item--tabs',
    reserveFreeShippingSpace: 'product-item--reserve-free-shipping-space',
    reserveFreeShippingSpaceWithoutLabel: 'product-item--reserve-free-shipping-space-without-label',
    stackedDiscount: 'product-item--stacked-discount',
    notifyReplacesPricing: 'product-item--notify-replaces-pricing',
    withFlowActions: 'product-item--with-flow-actions',
    outOfStock: 'product-item--out-of-stock',
    hasCountdown: 'product-item--has-countdown',
    reserveCountdownSpace: 'product-item--reserve-countdown-space',
};

const PRICE_ROW_HEIGHT_VAR = '--product-item-row-price-height';
const NOTIFY_ROW_HEIGHT_VAR = '--product-item-row-notify-height';
const SIMULATOR_ROW_HEIGHT_VAR = '--product-item-row-simulator-height';
const DISCOUNT_ROW_HEIGHT_VAR = '--product-item-row-discount-height';
const BUY_ROW_HEIGHT_VAR = '--product-item-row-buy-height';

const CARD_ROWS = [
    {
        cssVar: '--product-item-row-rating-height',
        selector: ':scope > .product-rating',
    },
    {
        cssVar: '--product-item-row-like-height',
        selector: ':scope > .product-list-like-swiper',
    },
    {
        cssVar: '--product-item-row-title-height',
        selector: ':scope > .product-title',
    },
    {
        cssVar: '--product-item-row-brand-height',
        selector: ':scope > .product-brand',
    },
    {
        cssVar: '--product-item-row-sold-height',
        selector: ':scope > .quantity_sold',
    },
    {
        cssVar: '--product-item-row-price-height',
        selector: ':scope > .product-price, :scope > .product-prices, :scope > .product-prices-content, :scope > .product-price-on-request__link',
    },
    {
        cssVar: '--product-item-row-notify-height',
        selector: ':scope > .product-notify',
    },
    {
        cssVar: '--product-item-row-simulator-height',
        selector: ':scope > .product-price-simulator, :scope > .product-simulator',
    },
    {
        cssVar: '--product-item-row-discount-height',
        selector: ':scope > .product-main-price-discount, :scope > .product-price-discount',
    },
    {
        cssVar: BUY_ROW_HEIGHT_VAR,
        selector: ':scope > .product-block-fixed, :scope > .product-block-hover',
    },
];

const FEATURED_DISCOUNT_SELECTOR = [
    ':scope > .product-price-discount.featured_price',
    ':scope > .product-main-price-discount.featured_price',
    ':scope > .product-main-price-discount.product-price-discount.featured_price',
    ':scope > .product-price > .product-price-discount.featured_price',
    ':scope > .product-price > .product-main-price-discount.featured_price',
    ':scope > .product-price > .product-main-price-discount.product-price-discount.featured_price',
].join(', ');

const FLOW_ACTIONS_SELECTOR = ':scope > .product-block-fixed, :scope > .product-block-hover, :scope > .product-consult-buy-whatsapp';
const OUT_OF_STOCK_SELECTOR = ':scope > .product-notify';
const LIKE_SELECTOR = ':scope > .product-list-like-swiper';
const PRICE_SELECTOR = ':scope > .product-price, :scope > .product-prices, :scope > .product-prices-content, :scope > .product-price-on-request__link';
const DISCOUNT_SELECTOR = ':scope > .product-main-price-discount, :scope > .product-price-discount.featured_price';
const NESTED_FEATURED_DISCOUNT_SELECTOR = ':scope > .product-price > .product-main-price-discount.featured_price, :scope > .product-price > .product-price-discount.featured_price, :scope > .product-price > .product-main-price-discount.product-price-discount.featured_price';
const FEATURED_LAYOUT_COLUMN_GAP = 16;
const COUNTDOWN_SELECTOR = '.product-countdown-timer, #countdown_timer_product_list';

let cardsSyncScheduled = false;
let cardsSyncInProgress = false;
let cardsObserverStarted = false;
let cardsListenersStarted = false;
let categoryGridListenersStarted = false;
let cardResizeObserverStarted = false;
let cardVisibilityObserverStarted = false;
let cardsObserverMuted = false;
let cardsObserverUnmuteTimer = null;
let cardResizeObserver = null;
let cardVisibilityObserver = null;
let shouldSyncAllCards = false;
const pendingCardScopes = new Set();
const observedCardCollections = new WeakSet();
const observedResizeTargets = new WeakSet();
const observedVisibilityTargets = new WeakSet();
const resizeTargetWidths = new WeakMap();

const isVisible = (element) => element instanceof HTMLElement && element.getClientRects().length > 0;

const isNearViewport = (element) => {
    if (!(element instanceof HTMLElement) || typeof IntersectionObserver === 'undefined') {
        return true;
    }

    const rect = element.getBoundingClientRect();
    const viewportMargin = 800;

    return rect.bottom >= -viewportMargin
        && rect.top <= (window.innerHeight + viewportMargin);
};

const hasMeaningfulDiscountValue = (element) => {
    if (!(element instanceof HTMLElement)) {
        return false;
    }

    const explicitValueElement = element.querySelector('.product-price-discount__value');

    if (explicitValueElement instanceof HTMLElement) {
        return explicitValueElement.textContent.trim().length > 0;
    }

    return element.textContent.trim().length > 0;
};

const getFirstMeaningfulElement = (scope, selector) => {
    if (!(scope instanceof HTMLElement)) {
        return null;
    }

    return Array.from(scope.querySelectorAll(selector)).find(hasMeaningfulDiscountValue) || null;
};

const setElementClassState = (element, className, isEnabled) => {
    if (!(element instanceof HTMLElement)) {
        return;
    }

    const hasClass = element.classList.contains(className);

    if (hasClass === isEnabled) {
        return;
    }

    element.classList.toggle(className, isEnabled);
};

const getCardInfo = (element) => {
    if (!(element instanceof HTMLElement)) {
        return null;
    }

    if (element.matches('.product-list-informations')) {
        return element;
    }

    return element.querySelector('.product-list-informations')
        || element.closest('.product-list-informations');
};

const getCardGrid = (element) => {
    if (!(element instanceof HTMLElement)) {
        return null;
    }

    if (element.matches('.product-grid')) {
        return element;
    }

    return element.querySelector('.product-grid') || element.closest('.product-grid');
};

const getCardRoot = (element) => {
    const info = getCardInfo(element);

    if (!info) {
        return null;
    }

    return info.closest('.product-item') || info.closest('.product-grid') || info;
};

const getCardRoots = (scope = document) => {
    const infos = scope instanceof HTMLElement && scope.matches('.product-list-informations')
        ? [scope]
        : Array.from(scope.querySelectorAll('.product-list-informations'));
    const roots = [];
    const seen = new Set();

    infos.forEach((info) => {
        const root = getCardRoot(info);

        if (!root || seen.has(root)) {
            return;
        }

        seen.add(root);
        roots.push(root);
    });

    return roots;
};

const isInsideLikeSwiper = (element) => {
    if (!(element instanceof HTMLElement)) {
        return false;
    }

    return element.matches('.product-list-like-swiper')
        || !!element.closest('.product-list-like-swiper');
};

const getCardCollection = (root) => {
    if (!(root instanceof HTMLElement)) {
        return null;
    }

    for (const selector of CARD_COLLECTIONS) {
        const collection = root.closest(selector);

        if (collection) {
            return collection;
        }
    }

    return null;
};

const LISTING_SHELF_PAGE_SELECTORS = [
    '.page-product-category',
    '.page-product-manufacturer-info',
    '.page-search',
    '.product-search-searchNewStructure',
    '.pageLayout--category',
    '.pageLayout--search',
    '.pageLayout--manufacturer',
].join(', ');

const LISTING_GRID_TRIGGER_SELECTOR = LISTING_SHELF_PAGE_SELECTORS
    .split(', ')
    .map((selector) => `${selector} .js-changeGrid`)
    .join(', ');

const isListingShelfCollection = (scope) => {
    if (!(scope instanceof HTMLElement)) {
        return false;
    }

    return scope.matches('#shelf-list-product') && !!scope.closest(LISTING_SHELF_PAGE_SELECTORS);
};

const getGroupedRowHeights = (states) => {
    const heights = CARD_ROWS.reduce((result, { cssVar }) => {
        result[cssVar] = 0;
        return result;
    }, {});

    states.forEach((state) => {
        CARD_ROWS.forEach(({ cssVar }) => {
            heights[cssVar] = Math.max(heights[cssVar], state.rowHeights[cssVar] || 0);
        });
    });

    return heights;
};

const getCssLengthInPixels = (value, referenceElement = document.documentElement) => {
    if (typeof value !== 'string') {
        return 0;
    }

    const normalizedValue = value.trim();

    if (!normalizedValue) {
        return 0;
    }

    if (normalizedValue.endsWith('rem')) {
        const rootFontSize = Number.parseFloat(
            window.getComputedStyle(document.documentElement).fontSize || '16'
        ) || 16;

        return (Number.parseFloat(normalizedValue) || 0) * rootFontSize;
    }

    if (normalizedValue.endsWith('px')) {
        return Number.parseFloat(normalizedValue) || 0;
    }

    if (normalizedValue.endsWith('em')) {
        const referenceFontSize = Number.parseFloat(
            window.getComputedStyle(referenceElement).fontSize || '16'
        ) || 16;

        return (Number.parseFloat(normalizedValue) || 0) * referenceFontSize;
    }

    return Number.parseFloat(normalizedValue) || 0;
};

const getBuyRowFallbackHeight = (referenceElement) => {
    const computedStyle = window.getComputedStyle(referenceElement || document.documentElement);
    const buttonHeight = getCssLengthInPixels(computedStyle.getPropertyValue('--spacing-10'), referenceElement);
    const buttonSpacing = getCssLengthInPixels(computedStyle.getPropertyValue('--spacing-2'), referenceElement);

    return Math.ceil(buttonHeight + buttonSpacing);
};

const getAppliedRowHeights = (state, groupedRowHeights, hasFlowActionsCard) => {
    const appliedRowHeights = {
        ...groupedRowHeights,
    };

    if (state.shouldMergeNotifyIntoPricingRows) {
        appliedRowHeights[PRICE_ROW_HEIGHT_VAR] = Math.max(
            groupedRowHeights[PRICE_ROW_HEIGHT_VAR] || 0,
            groupedRowHeights[NOTIFY_ROW_HEIGHT_VAR] || 0,
            state.rowHeights[NOTIFY_ROW_HEIGHT_VAR] || 0
        );
        appliedRowHeights[NOTIFY_ROW_HEIGHT_VAR] = 0;
    }

    if (
        hasFlowActionsCard
        && state.shouldMergeNotifyIntoPricingRows
        && !(appliedRowHeights[BUY_ROW_HEIGHT_VAR] > 0)
    ) {
        const fallbackBuyRowHeight = getBuyRowFallbackHeight(state.info || state.root);

        if (fallbackBuyRowHeight > 0) {
            appliedRowHeights[BUY_ROW_HEIGHT_VAR] = fallbackBuyRowHeight;
        }
    }

    return appliedRowHeights;
};

const getStatesByVisualRow = (states) => {
    const groups = [];
    const positionedStates = states
        .map((state) => ({
            state,
            top: Math.round(state.root.getBoundingClientRect().top || 0),
        }))
        .sort((a, b) => a.top - b.top);

    positionedStates.forEach(({ state, top }) => {
        const group = groups[groups.length - 1];

        if (group && Math.abs(group.top - top) <= 4) {
            group.states.push(state);
            return;
        }

        groups.push({
            states: [state],
            top,
        });
    });

    return groups.map((group) => group.states);
};

const isTabsCard = (info) => {
    if (!(info instanceof HTMLElement)) {
        return false;
    }

    return !!info.closest('.product-list-tabs .tab__panel');
};

const toggleCardState = (root, className, isEnabled) => {
    setElementClassState(root, className, isEnabled);
};

const isCarouselCard = (root) => {
    if (!(root instanceof HTMLElement)) {
        return false;
    }

    return root.matches('.swiper-slide, .carousel-item')
        || !!root.closest('.swiper-wrapper, .owl-stage');
};

const shouldMeasureCardRoot = (root) => {
    if (!(root instanceof HTMLElement)) {
        return false;
    }

    if (isCarouselCard(root)) {
        return isNearViewport(getCardCollection(root) || root);
    }

    return isNearViewport(root);
};

const syncCardMode = (root) => {
    const isCarousel = isCarouselCard(root);

    toggleCardState(root, CARD_STATES.carousel, isCarousel);
    toggleCardState(root, CARD_STATES.grid, !isCarousel);
};

const setCardVar = (root, cssVar, value) => {
    if (!(root instanceof HTMLElement)) {
        return;
    }

    if (root.style.getPropertyValue(cssVar) !== value) {
        root.style.setProperty(cssVar, value);
    }
};

const getElementVerticalMargins = (element) => {
    if (!(element instanceof HTMLElement)) {
        return 0;
    }

    const computedStyle = window.getComputedStyle(element);
    const marginTop = Number.parseFloat(computedStyle.marginTop || '0') || 0;
    const marginBottom = Number.parseFloat(computedStyle.marginBottom || '0') || 0;

    return marginTop + marginBottom;
};

const getElementOuterHeight = (element) => {
    if (!(element instanceof HTMLElement)) {
        return 0;
    }

    const height = element.getBoundingClientRect().height || 0;

    if (height <= 0) {
        return 0;
    }

    return Math.ceil(height + getElementVerticalMargins(element));
};

const getNestedPriceContentHeight = (priceElement, discountElement) => {
    if (!(priceElement instanceof HTMLElement)) {
        return 0;
    }

    const visibleChildren = Array.from(priceElement.children).filter((child) => {
        return child instanceof HTMLElement && child !== discountElement && isVisible(child);
    });

    if (!visibleChildren.length) {
        return getElementOuterHeight(priceElement);
    }

    const computedStyle = window.getComputedStyle(priceElement);
    const rowGap = Number.parseFloat(computedStyle.rowGap || computedStyle.gap || '0') || 0;
    const contentHeight = visibleChildren.reduce((height, child) => {
        return height + getElementOuterHeight(child);
    }, 0);

    return Math.ceil(contentHeight + (Math.max(visibleChildren.length - 1, 0) * rowGap));
};

const getNestedPriceContentWidth = (priceElement, discountElement) => {
    if (!(priceElement instanceof HTMLElement)) {
        return 0;
    }

    const visibleChildren = Array.from(priceElement.children).filter((child) => {
        return child instanceof HTMLElement && child !== discountElement && isVisible(child);
    });

    if (!visibleChildren.length) {
        return Math.ceil(priceElement.getBoundingClientRect().width || 0);
    }

    const computedStyle = window.getComputedStyle(priceElement);
    const columnGap = Number.parseFloat(computedStyle.columnGap || computedStyle.gap || '0') || 0;
    const contentWidth = visibleChildren.reduce((width, child) => {
        return width + (child.getBoundingClientRect().width || 0);
    }, 0);

    return Math.ceil(contentWidth + (Math.max(visibleChildren.length - 1, 0) * columnGap));
};

const getPrimaryPriceElement = (priceElement, discountElement) => {
    if (!(priceElement instanceof HTMLElement)) {
        return null;
    }

    return Array.from(priceElement.children).find((child) => {
        return child instanceof HTMLElement && child !== discountElement && isVisible(child);
    }) || priceElement;
};

const applyTabsCardLayout = (root, info) => {
    if (!(root instanceof HTMLElement) || !(info instanceof HTMLElement)) {
        return;
    }

    toggleCardState(root, CARD_STATES.tabs, isTabsCard(info));
};

const syncLegacyCardState = (state, hasFreeShippingCard, hasFlowActionsCard) => {
    const info = state.info;
    const grid = state.grid;

    if (info instanceof HTMLElement) {
        setElementClassState(info, 'product-list-informations--layout-1', true);
        setElementClassState(info, 'product-list-informations--with-flow-actions', hasFlowActionsCard);
        setElementClassState(info, 'product-list-informations--stacked-discount', state.shouldStackDiscount);
        applyTabsCardLayout(state.root, info);
    }

    if (grid instanceof HTMLElement) {
        setElementClassState(grid, 'product-grid--reserve-free-shipping-space', hasFreeShippingCard);
        setElementClassState(
            grid,
            'product-grid--reserve-free-shipping-space-without-label',
            hasFreeShippingCard && !state.hasOwnFreeShipping
        );
    }
};

const syncCountdownCardState = (state, hasCountdownCard) => {
    const grid = state.grid;

    if (grid instanceof HTMLElement) {
        setElementClassState(grid, 'product-grid--has-countdown', state.hasCountdown);
        setElementClassState(grid, 'product-grid--reserve-countdown-space', hasCountdownCard);
    }
};

const measureCard = (root, shouldMeasure = shouldMeasureCardRoot(root)) => {
    const info = getCardInfo(root);

    if (!(info instanceof HTMLElement)) {
        return null;
    }

    const grid = getCardGrid(root);

    const isCardVisible = shouldMeasure && isVisible(root) && isVisible(info);
    const hasCustomPrice = !!info.querySelector(':scope > .product-price--custom-layout');

    const isOutOfStock = !!info.querySelector(OUT_OF_STOCK_SELECTOR);
    const featuredDiscountElement = hasCustomPrice
        ? null
        : getFirstMeaningfulElement(info, FEATURED_DISCOUNT_SELECTOR);
    const hasFeaturedDiscount = !!featuredDiscountElement;
    const hasFlowActions = !!info.querySelector(FLOW_ACTIONS_SELECTOR);
    const hasOwnFreeShipping = !!(grid || root).querySelector('.product-shipping__free-label');
    const hasCountdown = !!root.querySelector(COUNTDOWN_SELECTOR);

    let shouldStackDiscount = hasCustomPrice
        ? false
        : root.classList.contains(CARD_STATES.stackedDiscount);
    const rowHeights = CARD_ROWS.reduce((heights, { cssVar }) => {
        heights[cssVar] = 0;
        return heights;
    }, {});

    if (!isCardVisible) {
        return {
            grid,
            hasFeaturedDiscount,
            hasFlowActions,
            hasCountdown,
            hasCustomPrice,
            hasOwnFreeShipping,
            info,
            isOutOfStock,
            isVisible: isCardVisible,
            root,
            rowHeights,
            shouldMergeNotifyIntoPricingRows: root.classList.contains(CARD_STATES.notifyReplacesPricing),
            shouldStackDiscount,
        };
    }

    const priceElement = info.querySelector(PRICE_SELECTOR);
    const nestedFeaturedDiscountElement = hasCustomPrice
        ? null
        : getFirstMeaningfulElement(info, NESTED_FEATURED_DISCOUNT_SELECTOR);
    const discountElement = getFirstMeaningfulElement(info, DISCOUNT_SELECTOR) || nestedFeaturedDiscountElement;

    if (!hasCustomPrice && priceElement && discountElement) {
        const infoComputedStyle = window.getComputedStyle(info);
        const horizontalPadding = (
            (Number.parseFloat(infoComputedStyle.paddingLeft || '0') || 0)
            + (Number.parseFloat(infoComputedStyle.paddingRight || '0') || 0)
        );
        const cardWidth = Math.max((info.clientWidth || 0) - horizontalPadding, 0);
        const discountWidth = discountElement.getBoundingClientRect().width || 0;
        const priceWidth = nestedFeaturedDiscountElement instanceof HTMLElement
            ? getNestedPriceContentWidth(priceElement, nestedFeaturedDiscountElement)
            : (priceElement.getBoundingClientRect().width || 0);

        shouldStackDiscount = (priceWidth + discountWidth + FEATURED_LAYOUT_COLUMN_GAP) > cardWidth;

        if (hasFeaturedDiscount) {
            const priceReferenceElement = nestedFeaturedDiscountElement instanceof HTMLElement
                ? getPrimaryPriceElement(priceElement, nestedFeaturedDiscountElement)
                : priceElement;
            const priceRect = priceReferenceElement instanceof HTMLElement
                ? priceReferenceElement.getBoundingClientRect()
                : null;
            const discountRect = discountElement.getBoundingClientRect();
            const infoRect = info.getBoundingClientRect();

            if (priceRect && ((discountRect.top - priceRect.top) > 2 || discountRect.right > (infoRect.right + 1))) {
                shouldStackDiscount = true;
            }
        }
    } else {
        shouldStackDiscount = false;
    }

    const shouldMergeNotifyIntoPricingRows = (
        isOutOfStock
        && !isVisible(priceElement)
        && !isVisible(discountElement)
        && !isVisible(info.querySelector(':scope > .product-price-simulator, :scope > .product-simulator'))
    );

    return {
        grid,
        hasFeaturedDiscount,
        hasFlowActions,
        hasCountdown,
        hasCustomPrice,
        hasOwnFreeShipping,
        info,
        isOutOfStock,
        isVisible: isCardVisible,
        root,
        rowHeights,
        shouldMergeNotifyIntoPricingRows,
        shouldStackDiscount,
    };
};

const measureCardRowsFromAppliedLayout = (state) => {
    const info = state?.info;

    if (!(info instanceof HTMLElement) || !isVisible(info)) {
        return state?.rowHeights || CARD_ROWS.reduce((heights, { cssVar }) => {
            heights[cssVar] = 0;
            return heights;
        }, {});
    }

    const rowHeights = CARD_ROWS.reduce((heights, { cssVar }) => {
        heights[cssVar] = 0;
        return heights;
    }, {});

    CARD_ROWS.forEach(({ cssVar, selector }) => {
        const element = info.querySelector(selector);

        if (!element) {
            return;
        }

        rowHeights[cssVar] = getElementOuterHeight(element);
    });

    const priceElement = info.querySelector(PRICE_SELECTOR);
    const nestedFeaturedDiscountElement = state?.hasCustomPrice
        ? null
        : getFirstMeaningfulElement(info, NESTED_FEATURED_DISCOUNT_SELECTOR);

    if (priceElement instanceof HTMLElement && nestedFeaturedDiscountElement instanceof HTMLElement) {
        rowHeights[PRICE_ROW_HEIGHT_VAR] = getNestedPriceContentHeight(
            priceElement,
            nestedFeaturedDiscountElement
        );

        rowHeights[DISCOUNT_ROW_HEIGHT_VAR] = state.shouldStackDiscount
            ? getElementOuterHeight(nestedFeaturedDiscountElement)
            : 0;
    }

    if (state?.hasFeaturedDiscount && !state?.shouldStackDiscount) {
        const priceElementHeight = priceElement instanceof HTMLElement
            ? getElementOuterHeight(priceElement)
            : 0;

        rowHeights[PRICE_ROW_HEIGHT_VAR] = Math.max(
            rowHeights[PRICE_ROW_HEIGHT_VAR] || 0,
            rowHeights[DISCOUNT_ROW_HEIGHT_VAR] || 0,
            priceElementHeight
        );
        rowHeights[DISCOUNT_ROW_HEIGHT_VAR] = 0;
    }

    return rowHeights;
};

const syncCardCollection = (scope) => {
    const roots = scope instanceof HTMLElement && (scope.matches('.product-item') || scope.matches('.product-grid'))
        ? [scope]
        : getCardRoots(scope);

    if (!roots.length) {
        return;
    }

    const measurableRoots = new Set(roots.filter(shouldMeasureCardRoot));

    roots.forEach((root) => {
        syncCardMode(root);
        applyTabsCardLayout(root, getCardInfo(root));

        if (measurableRoots.has(root)) {
            toggleCardState(root, CARD_STATES.stackedDiscount, false);
        }
    });

    const states = roots
        .map((root) => measureCard(root, measurableRoots.has(root)))
        .filter((state) => state !== null);

    if (!states.length) {
        return;
    }

    const visibleStates = states.filter((state) => state.isVisible);

    if (!visibleStates.length) {
        return;
    }

    const hasFreeShippingCard = states.some((state) => state.hasOwnFreeShipping);
    const hasFlowActionsCard = states.some((state) => state.hasFlowActions);
    const hasCountdownCard = states.some((state) => state.hasCountdown);

    states.forEach((state) => {
        syncLegacyCardState(state, hasFreeShippingCard, hasFlowActionsCard);
        toggleCardState(state.root, CARD_STATES.customPrice, state.hasCustomPrice);
        syncCountdownCardState(state, hasCountdownCard);
        toggleCardState(state.root, CARD_STATES.layout1, true);
        toggleCardState(state.root, CARD_STATES.featuredDiscount, state.hasFeaturedDiscount);
        toggleCardState(state.root, CARD_STATES.withFlowActions, hasFlowActionsCard);
        toggleCardState(state.root, CARD_STATES.outOfStock, state.isOutOfStock);
        toggleCardState(state.root, CARD_STATES.hasCountdown, state.hasCountdown);
        toggleCardState(state.root, CARD_STATES.reserveCountdownSpace, hasCountdownCard);
        toggleCardState(state.root, CARD_STATES.stackedDiscount, state.shouldStackDiscount);
        toggleCardState(state.root, CARD_STATES.notifyReplacesPricing, state.shouldMergeNotifyIntoPricingRows);
        toggleCardState(state.root, CARD_STATES.reserveFreeShippingSpace, hasFreeShippingCard);
        toggleCardState(
            state.root,
            CARD_STATES.reserveFreeShippingSpaceWithoutLabel,
            hasFreeShippingCard && !state.hasOwnFreeShipping
        );
    });

    visibleStates.forEach((state) => {
        state.rowHeights = measureCardRowsFromAppliedLayout(state);
    });

    const stateRowHeights = new Map();
    const defaultRowHeights = getGroupedRowHeights(visibleStates);

    if (isListingShelfCollection(scope)) {
        getStatesByVisualRow(visibleStates).forEach((groupStates) => {
            const groupedHeights = getGroupedRowHeights(groupStates);

            groupStates.forEach((state) => {
                stateRowHeights.set(state, groupedHeights);
            });
        });
    }

    const statesToApplyHeights = isListingShelfCollection(scope)
        ? visibleStates
        : states;

    statesToApplyHeights.forEach((state) => {
        const groupedRowHeights = stateRowHeights.get(state) || defaultRowHeights;
        const appliedRowHeights = getAppliedRowHeights(state, groupedRowHeights, hasFlowActionsCard);

        CARD_ROWS.forEach(({ cssVar }) => {
            setCardVar(state.root, cssVar, `${appliedRowHeights[cssVar]}px`);
        });
    });
};

const getCardScopes = (scope = document) => {
    const scopes = new Set();

    getCardRoots(scope).forEach((root) => {
        scopes.add(getCardCollection(root) || root);
    });

    return scopes;
};

const getCardScopeFromElement = (element) => {
    if (!(element instanceof HTMLElement)) {
        return null;
    }

    const info = element.matches('.product-list-informations')
        ? element
        : element.closest('.product-list-informations');
    const root = info ? getCardRoot(info) : null;

    if (root) {
        return getCardCollection(root) || root;
    }

    if (
        !element.matches('.product-item, .product-grid')
        && !CARD_COLLECTIONS.some((selector) => element.matches(selector))
    ) {
        return null;
    }

    const nestedRoot = getCardRoots(element)[0];

    return nestedRoot ? (getCardCollection(nestedRoot) || nestedRoot) : null;
};

const observeCardScope = (scope) => {
    if (!(scope instanceof HTMLElement)) {
        return;
    }

    const observer = window.__productListModuleObserver;

    if (observer instanceof MutationObserver && !observedCardCollections.has(scope)) {
        observer.observe(scope, {
            attributeFilter: ['class', 'hidden', 'style'],
            attributes: true,
            subtree: true,
        });
        observedCardCollections.add(scope);
    }

    if (
        typeof ResizeObserver !== 'undefined'
        && cardResizeObserver instanceof ResizeObserver
        && !observedResizeTargets.has(scope)
    ) {
        observedResizeTargets.add(scope);
        resizeTargetWidths.set(scope, Math.round(scope.getBoundingClientRect().width || 0));
        cardResizeObserver.observe(scope);
    }

    if (
        typeof IntersectionObserver !== 'undefined'
        && cardVisibilityObserver instanceof IntersectionObserver
    ) {
        getCardRoots(scope).forEach((root) => {
            if (observedVisibilityTargets.has(root)) {
                return;
            }

            observedVisibilityTargets.add(root);
            cardVisibilityObserver.observe(root);
        });
    }
};

const syncCards = () => {
    if (cardsSyncInProgress) {
        return;
    }

    if (cardsObserverUnmuteTimer) {
        window.clearTimeout(cardsObserverUnmuteTimer);
        cardsObserverUnmuteTimer = null;
    }

    cardsObserverMuted = true;
    cardsSyncInProgress = true;

    try {
        const scopes = shouldSyncAllCards
            ? getCardScopes()
            : new Set(pendingCardScopes);

        shouldSyncAllCards = false;
        pendingCardScopes.clear();

        scopes.forEach((scope) => {
            if (!(scope instanceof HTMLElement) || !scope.isConnected) {
                return;
            }

            observeCardScope(scope);
            syncCardCollection(scope);
        });
    } finally {
        cardsSyncInProgress = false;
        cardsObserverUnmuteTimer = window.setTimeout(() => {
            cardsObserverMuted = false;
            cardsObserverUnmuteTimer = null;
        }, 0);
    }
};

const scheduleCardsSync = (scope = null) => {
    if (scope instanceof HTMLElement) {
        pendingCardScopes.add(scope);
    } else {
        shouldSyncAllCards = true;
        pendingCardScopes.clear();
    }

    if (cardsSyncScheduled) {
        return;
    }

    cardsSyncScheduled = true;

    window.requestAnimationFrame(() => {
        cardsSyncScheduled = false;
        syncCards();
    });
};

const observeCards = () => {
    if (cardsObserverStarted) {
        return;
    }

    const observer = new MutationObserver((mutations) => {
        if (cardsSyncInProgress || cardsObserverMuted) {
            return;
        }

        const scopes = new Set();

        mutations.forEach((mutation) => {
            if (mutation.type === 'childList') {
                const target = mutation.target instanceof HTMLElement
                    ? mutation.target
                    : mutation.target.parentElement;
                const targetScope = getCardScopeFromElement(target);

                if (targetScope && !isInsideLikeSwiper(target)) {
                    scopes.add(targetScope);
                }

                mutation.addedNodes.forEach((node) => {
                    if (!(node instanceof HTMLElement) || isInsideLikeSwiper(node)) {
                        return;
                    }

                    const addedScopes = getCardScopes(node);

                    if (!addedScopes.size) {
                        const addedScope = getCardScopeFromElement(node);

                        if (addedScope) {
                            addedScopes.add(addedScope);
                        }
                    }

                    addedScopes.forEach((scope) => scopes.add(scope));
                });

                return;
            }

            if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
                if (isInsideLikeSwiper(mutation.target)) {
                    return;
                }

                const scope = getCardScopeFromElement(mutation.target);

                if (scope) {
                    scopes.add(scope);
                }
            }
        });

        scopes.forEach((scope) => scheduleCardsSync(scope));
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true,
    });

    window.__productListModuleObserver = observer;
    cardsObserverStarted = true;
};

const bindCardEvents = () => {
    if (cardsListenersStarted) {
        return;
    }

    window.addEventListener('load', scheduleCardsSync);

    if (typeof ResizeObserver === 'undefined') {
        window.addEventListener('resize', scheduleCardsSync);
    }

    if (document.fonts?.ready) {
        document.fonts.ready.then(scheduleCardsSync);
    }

    cardsListenersStarted = true;
};

const bindListingGridEvents = () => {
    if (categoryGridListenersStarted) {
        return;
    }

    document.addEventListener('click', (event) => {
        const trigger = event.target instanceof Element
            ? event.target.closest(LISTING_GRID_TRIGGER_SELECTOR)
            : null;

        if (!(trigger instanceof HTMLElement)) {
            return;
        }

        const page = trigger.closest(LISTING_SHELF_PAGE_SELECTORS);
        const shelf = page?.querySelector('#shelf-list-product');

        scheduleCardsSync(shelf);
        window.setTimeout(() => scheduleCardsSync(shelf), 360);
    });

    categoryGridListenersStarted = true;
};

const bindCardResizeObserver = () => {
    if (cardResizeObserverStarted || typeof ResizeObserver === 'undefined') {
        return;
    }

    cardResizeObserver = new ResizeObserver((entries) => {
        if (cardsSyncInProgress || cardsObserverMuted) {
            return;
        }

        entries.forEach((entry) => {
            if (!(entry.target instanceof HTMLElement)) {
                return;
            }

            const nextWidth = Math.round(entry.contentRect.width || 0);
            const previousWidth = resizeTargetWidths.get(entry.target);
            resizeTargetWidths.set(entry.target, nextWidth);

            if (typeof previousWidth === 'number' && previousWidth !== nextWidth) {
                scheduleCardsSync(entry.target);
            }
        });
    });

    cardResizeObserverStarted = true;
};

const bindCardVisibilityObserver = () => {
    if (cardVisibilityObserverStarted || typeof IntersectionObserver === 'undefined') {
        return;
    }

    cardVisibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting || !(entry.target instanceof HTMLElement)) {
                return;
            }

            scheduleCardsSync(getCardCollection(entry.target) || entry.target);
        });
    }, {
        rootMargin: '800px 0px',
    });

    cardVisibilityObserverStarted = true;
};

export const initProductListModule = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }

    if (window.__productListModuleInitialized) {
        return;
    }

    window.__productListModuleInitialized = true;

    bindCardEvents();
    bindListingGridEvents();
    bindCardResizeObserver();
    bindCardVisibilityObserver();

    if (document.body) {
        observeCards();
    } else {
        document.addEventListener('DOMContentLoaded', observeCards, { once: true });
    }

    window.syncProductListModule = scheduleCardsSync;
    scheduleCardsSync();
};

export const initProductListsNearViewport = () => {
    const is_lime = document.body.classList.contains('theme-_lime');

    // Lime não utiliza essa inicialização
    if (is_lime) {
        return;
    }

    const product_lists = document.querySelectorAll('.productList');

    if (
        product_lists.length === 0 ||
        typeof IntersectionObserver === 'undefined'
    ) {
        initProductListModule();
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
            return;
        }

        observer.disconnect();
        requestAnimationFrame(initProductListModule);
    });

    product_lists.forEach((product_list) => observer.observe(product_list));
};

export const syncProductListModule = () => {
    scheduleCardsSync();
};
